import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Rocket, Loader2, Plus, Trash2, FileText, Target, ChevronRight, ChevronLeft, FileDown } from "lucide-react";

const COLUMNS = [
  { id: "backlog", label: "Backlog" },
  { id: "inprogress", label: "In Progress" },
  { id: "done", label: "Done" },
];

const CAT_COLORS = {
  Fundraising: "#D4AF37",
  Product: "#6EA8FE",
  Brand: "#E5739B",
  Team: "#5BD1A6",
  Operations: "#B99CFF",
  General: "#A1A1AA",
};

function renderMarkdown(md) {
  const lines = md.split("\n");
  const out = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      out.push(<ul key={`ul-${key}`}>{list.map((t, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(t) }} />)}</ul>);
      list = [];
    }
  };
  const inline = (t) => t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  lines.forEach((raw, i) => {
    const l = raw.trim();
    if (l.startsWith("### ")) { flush(i); out.push(<h3 key={i} dangerouslySetInnerHTML={{ __html: inline(l.slice(4)) }} />); }
    else if (l.startsWith("## ")) { flush(i); out.push(<h2 key={i} dangerouslySetInnerHTML={{ __html: inline(l.slice(3)) }} />); }
    else if (l.startsWith("# ")) { flush(i); out.push(<h1 key={i} dangerouslySetInnerHTML={{ __html: inline(l.slice(2)) }} />); }
    else if (l.startsWith("- ") || l.startsWith("* ")) { list.push(l.slice(2)); }
    else if (l === "") { flush(i); }
    else { flush(i); out.push(<p key={i} dangerouslySetInnerHTML={{ __html: inline(l) }} />); }
  });
  flush("end");
  return out;
}

export default function LaunchPage() {
  const [tasks, setTasks] = useState([]);
  const [plan, setPlan] = useState(null);
  const [fund, setFund] = useState({ goal: 500000, raised: 0, note: "" });
  const [genTasks, setGenTasks] = useState(false);
  const [genPlan, setGenPlan] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [deckLoading, setDeckLoading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  useEffect(() => {
    api.getTasks().then(setTasks).catch(() => {});
    api.getPlan().then((p) => p && p.content && setPlan(p)).catch(() => {});
    api.getFundraising().then(setFund).catch(() => {});
  }, []);

  const generateTasks = async () => {
    setGenTasks(true);
    try {
      const created = await api.generateTasks();
      setTasks((t) => [...t, ...created]);
      toast.success(`${created.length} launch tasks drafted`);
    } catch (e) {
      toast.error("Could not generate tasks", { description: e?.response?.data?.detail });
    } finally { setGenTasks(false); }
  };

  const generatePlan = async () => {
    setGenPlan(true);
    try {
      const p = await api.generatePlan();
      setPlan(p);
      toast.success("Launch & capital strategy ready");
    } catch (e) {
      toast.error("Could not generate plan", { description: e?.response?.data?.detail });
    } finally { setGenPlan(false); }
  };

  const addTask = async () => {
    if (!newTitle.trim()) return;
    const t = await api.createTask({ title: newTitle.trim(), column: "backlog", category: "General" });
    setTasks((x) => [...x, t]);
    setNewTitle("");
  };

  const move = async (task, dir) => {
    const idx = COLUMNS.findIndex((c) => c.id === task.column);
    const next = COLUMNS[idx + dir];
    if (!next) return;
    const updated = await api.patchTask(task.id, { column: next.id });
    setTasks((x) => x.map((t) => (t.id === task.id ? updated : t)));
  };

  const dropOnColumn = async (colId) => {
    setOverCol(null);
    const task = tasks.find((t) => t.id === dragId);
    setDragId(null);
    if (!task || task.column === colId) return;
    setTasks((x) => x.map((t) => (t.id === task.id ? { ...t, column: colId } : t)));
    try {
      await api.patchTask(task.id, { column: colId });
    } catch {
      toast.error("Could not move task");
      api.getTasks().then(setTasks);
    }
  };

  const downloadDeck = async () => {
    setDeckLoading(true);
    try {
      const blob = await api.downloadDeck();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "LUTAZZI_Pitch_Deck.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Pitch deck exported");
    } catch (e) {
      toast.error("Could not export deck", { description: e?.response?.data?.detail });
    } finally {
      setDeckLoading(false);
    }
  };

  const removeTask = async (id) => {
    await api.deleteTask(id);
    setTasks((x) => x.filter((t) => t.id !== id));
  };

  const saveFund = async (patch) => {
    const updated = await api.patchFundraising(patch);
    setFund(updated);
  };

  const pct = useMemo(() => {
    if (!fund.goal) return 0;
    return Math.min(100, Math.round((Number(fund.raised) / Number(fund.goal)) * 100));
  }, [fund]);

  const money = (n) => "$" + Number(n || 0).toLocaleString();

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 min-h-screen">
      <header className="pb-6 border-b border-white/10">
        <div className="overline-label">Capital & Launch Engine</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mt-1">Make It Real</h2>
        <p className="text-neutral-400 mt-2 max-w-2xl">
          Turn the concept house into a company — fundraising, prototyping, team and a 90-day runway
          to launch. ATELIER drafts the strategy; you drive it forward.
        </p>
      </header>

      {/* Fundraising tracker */}
      <div className="card-lux rounded-xl p-6 sm:p-8 mt-8" data-testid="fundraising-card">
        <div className="flex items-center gap-2 overline-label"><Target size={13} className="text-amber-400" /> Fundraising Target</div>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-3">
          <div>
            <div className="font-serif text-4xl text-neutral-100">{money(fund.raised)}</div>
            <div className="text-neutral-500 font-mono text-sm mt-1">raised of {money(fund.goal)} goal · {pct}%</div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <div>
              <label className="overline-label text-[9px]">Raised</label>
              <input
                type="number"
                data-testid="fund-raised-input"
                defaultValue={fund.raised}
                onBlur={(e) => saveFund({ raised: Number(e.target.value) })}
                className="w-32 bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-3 py-2 mt-1 text-neutral-100 font-mono outline-none"
              />
            </div>
            <div>
              <label className="overline-label text-[9px]">Goal</label>
              <input
                type="number"
                data-testid="fund-goal-input"
                defaultValue={fund.goal}
                onBlur={(e) => saveFund({ goal: Number(e.target.value) })}
                className="w-32 bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-3 py-2 mt-1 text-neutral-100 font-mono outline-none"
              />
            </div>
          </div>
        </div>
        <div className="h-2 bg-[#1A1A1F] rounded-full mt-5 overflow-hidden">
          <div className="h-full bg-amber-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Business plan */}
      <div className="card-lux rounded-xl p-6 sm:p-8 mt-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 overline-label"><FileText size={13} className="text-amber-400" /> Launch & Capital Strategy</div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="export-deck-btn" onClick={downloadDeck} disabled={deckLoading} className="rounded-lg px-5 py-2.5 text-sm flex items-center gap-2 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors">
              {deckLoading ? <Loader2 className="animate-spin" size={16} /> : <FileDown size={16} />}
              Export Pitch Deck
            </button>
            <button data-testid="generate-plan-btn" onClick={generatePlan} disabled={genPlan} className="btn-gold rounded-lg px-5 py-2.5 text-sm flex items-center gap-2">
              {genPlan ? <Loader2 className="animate-spin" size={16} /> : <Rocket size={16} />}
              {plan ? "Regenerate Strategy" : "Draft My Strategy"}
            </button>
          </div>
        </div>
        {plan ? (
          <div className="prose-lux mt-5 max-w-none" data-testid="plan-content">{renderMarkdown(plan.content)}</div>
        ) : (
          <p className="text-neutral-500 mt-4">No strategy yet. Let ATELIER draft your path from concept to company.</p>
        )}
      </div>

      {/* Task board */}
      <div className="flex items-center justify-between gap-4 flex-wrap mt-10 mb-4">
        <h3 className="font-serif text-2xl">Master Task Board</h3>
        <button data-testid="generate-tasks-btn" onClick={generateTasks} disabled={genTasks} className="btn-gold rounded-lg px-5 py-2.5 text-sm flex items-center gap-2">
          {genTasks ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
          Draft Launch Tasks
        </button>
      </div>

      <div className="flex gap-2 mb-5">
        <input
          data-testid="new-task-input"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTask()}
          placeholder="Add your own task…"
          className="flex-1 bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-4 py-2.5 text-neutral-100 placeholder:text-neutral-600 outline-none"
        />
        <button onClick={addTask} className="btn-gold rounded-lg px-4" data-testid="add-task-btn"><Plus size={18} /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {COLUMNS.map((col) => {
          const colTasks = tasks.filter((t) => t.column === col.id);
          return (
            <div
              key={col.id}
              onDragOver={(e) => { e.preventDefault(); setOverCol(col.id); }}
              onDragLeave={() => setOverCol((c) => (c === col.id ? null : c))}
              onDrop={() => dropOnColumn(col.id)}
              className={`bg-[#0F0F12] border rounded-xl p-4 transition-colors ${overCol === col.id ? "border-amber-500/60 bg-[#141017]" : "border-white/10"}`}
              data-testid={`column-${col.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="overline-label">{col.label}</span>
                <span className="text-xs font-mono text-neutral-500">{colTasks.length}</span>
              </div>
              <div className="space-y-3 min-h-[60px]">
                {colTasks.map((t) => (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                    className={`card-lux rounded-lg p-3 cursor-grab active:cursor-grabbing ${dragId === t.id ? "opacity-40" : ""}`}
                    data-testid={`task-${t.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2 h-2 rounded-full" style={{ background: CAT_COLORS[t.category] || "#A1A1AA" }} />
                      <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{t.category}</span>
                    </div>
                    <p className="text-sm text-neutral-100 font-medium">{t.title}</p>
                    {t.description && <p className="text-xs text-neutral-500 mt-1 leading-relaxed">{t.description}</p>}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex gap-1">
                        <button onClick={() => move(t, -1)} disabled={col.id === "backlog"} className="p-1 text-neutral-500 hover:text-amber-400 disabled:opacity-20" data-testid={`task-prev-${t.id}`}><ChevronLeft size={16} /></button>
                        <button onClick={() => move(t, 1)} disabled={col.id === "done"} className="p-1 text-neutral-500 hover:text-amber-400 disabled:opacity-20" data-testid={`task-next-${t.id}`}><ChevronRight size={16} /></button>
                      </div>
                      <button onClick={() => removeTask(t.id)} className="p-1 text-neutral-600 hover:text-red-400" data-testid={`task-delete-${t.id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
                {colTasks.length === 0 && <p className="text-xs text-neutral-700 text-center py-6">Empty</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
